/**
 * Focus Trap Utility
 * Traps keyboard focus within a container element for accessibility
 */

class FocusTrap {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      initialFocus: options.initialFocus || null,
      onEscape: options.onEscape || null,
      returnFocus: options.returnFocus !== false
    };

    this.previouslyFocused = null;
    this.isActive = false;

    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  getFocusableElements() {
    const selector = [
      'a[href]:not([disabled]):not([tabindex="-1"])',
      'button:not([disabled]):not([tabindex="-1"])',
      'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"]):not([disabled])',
      '[contenteditable]:not([tabindex="-1"])'
    ].join(', ');

    const elements = Array.from(this.container.querySelectorAll(selector));

    // Filter out elements that are not visible
    return elements.filter(el => {
      return el.offsetParent !== null &&
             getComputedStyle(el).visibility !== 'hidden' &&
             getComputedStyle(el).display !== 'none';
    });
  }

  handleKeyDown(e) {
    if (!this.isActive) return;

    if (e.key === 'Tab') {
      const focusableElements = this.getFocusableElements();

      if (focusableElements.length === 0) {
        e.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (e.shiftKey) {
        // Shift + Tab: Move focus backward
        if (activeElement === firstElement || !this.container.contains(activeElement)) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: Move focus forward
        if (activeElement === lastElement || !this.container.contains(activeElement)) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }

    if (e.key === 'Escape' && this.options.onEscape) {
      this.options.onEscape(e);
    }
  }

  activate() {
    if (this.isActive) return;

    this.isActive = true;
    this.previouslyFocused = document.activeElement;

    document.addEventListener('keydown', this.handleKeyDown);

    // Set initial focus
    requestAnimationFrame(() => {
      if (this.options.initialFocus) {
        const initialElement = typeof this.options.initialFocus === 'string'
          ? this.container.querySelector(this.options.initialFocus)
          : this.options.initialFocus;

        if (initialElement) {
          initialElement.focus();
          return;
        }
      }

      // Focus first focusable element
      const focusableElements = this.getFocusableElements();
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    });
  }

  deactivate() {
    if (!this.isActive) return;

    this.isActive = false;
    document.removeEventListener('keydown', this.handleKeyDown);

    // Return focus to previously focused element
    if (this.options.returnFocus && this.previouslyFocused) {
      this.previouslyFocused.focus();
    }

    this.previouslyFocused = null;
  }

  updateContainer(container) {
    this.container = container;
  }
}

// Export for use in other files
window.FocusTrap = FocusTrap;
